<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { useBannerStore } from './store/useBannerStore'
import type { IBanner } from './types'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import LayoutTable from '@/components/CDF/LayoutTable.vue'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'banner',
  },
})

const store = useBannerStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
} = store

// Função para retornar cor do status de pagamento
function getStatusColor(status?: string) {
  const colors: Record<string, string> = {
    pending: 'warning',
    received: 'info',
    confirmed: 'success',
    overdue: 'error',
    refunded: 'secondary',
    cancelled: 'error',
  }

  return colors[status || ''] || 'default'
}

// Função para formatar data
function formatDate(date?: string) {
  if (!date)
    return ''

  const [year, month, day] = date.split('-')

  return `${day}/${month}/${year}`
}

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Título',
      key: 'titulo',
      sortable: true,
    },
    {
      title: 'Banner',
      key: 'banner',
      sortable: false,
    },
    {
      title: 'Status',
      key: 'ativo',
      sortable: true,
    },
    {
      title: 'Cobrança',
      key: 'gerar_cobranca',
      sortable: true,
    },
    {
      title: 'Status Pagamento',
      key: 'status_pagamento',
      sortable: true,
    },
    {
      title: 'Validade',
      key: 'validade',
      sortable: false,
    },
  ] as IHeader[]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Titulo',
      value: 'titulo',
    },
  ] as ITerm[]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-eye',
    color: 'info',
    can: {
      action: 'edit',
      subject: 'banner',
    },
    to: (item: IBanner) => `/admin/banner/editar/${item.id}`,
  },
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'banner',
    },
    to: (item: IBanner) => `/admin/banner/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'banner',
    },
    onClick: (item: IBanner) => dialogDestroy(item.id),
  },
]

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Banners"
    new-item="/admin/banner/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.banner="{ item }">
      <a
        :href="item.banner"
        target="_blank"
        rel="noopener noreferrer"
      >
        <VImg
          :src="item.banner"
          width="100"
          height="60"
          cover
          class="rounded"
        />
      </a>
    </template>

    <template #item.ativo="{ item }">
      <VChip
        :color="item.ativo ? 'success' : 'error'"
        size="small"
        variant="tonal"
      >
        {{ item.ativo ? 'Ativo' : 'Inativo' }}
      </VChip>
    </template>

    <template #item.gerar_cobranca="{ item }">
      <VChip
        :color="item.gerar_cobranca ? 'primary' : 'default'"
        size="small"
        variant="tonal"
      >
        {{ item.gerar_cobranca ? 'Sim' : 'Não' }}
      </VChip>
    </template>

    <template #item.status_pagamento="{ item }">
      <VChip
        v-if="item.gerar_cobranca && item.status_pagamento"
        :color="getStatusColor(item.status_pagamento)"
        size="small"
        variant="tonal"
      >
        {{ item.status_pagamento_label || item.status_pagamento }}
      </VChip>
      <span
        v-else
        class="text-caption text-disabled"
      >
        —
      </span>
    </template>

    <template #item.validade="{ item }">
      <div class="text-caption">
        <div v-if="item.data_inicio">
          <strong>Início:</strong> {{ formatDate(item.data_inicio) }}
        </div>
        <div v-if="item.data_fim">
          <strong>Fim:</strong> {{ formatDate(item.data_fim) }}
        </div>
        <span
          v-if="!item.data_inicio && !item.data_fim"
          class="text-disabled"
        >
          Sem validade
        </span>
      </div>
    </template>
  </LayoutTable>
</template>
